/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { FSMFactoryModule } from "@vlinder-web/state-machine-factory-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";
import {
    TrafficLightService,
    DeviceInfoService,
    DummyRestService,
    DummyGraphQLService,
    LoginService,
    SignUpService,
    ResetPasswordService,
    ChangePasswordService,
    AuthService,
    ApplicationFeeDurationService,
    ApplicationFormService,
    ApplicationStatusService,
    UploadPaymentService,
    GetCertificatesTableService,
    ProfileService,
    DownloadEpccService,
    EmailEpccService,
    ApplicationListService,
    UpdateApplicationFormService
} from '../services';

import { DEFAULT_APP_CONFIG } from "../config/default.config";
import { StorageModule } from "@vlinder-web/storage-module-react";

export class VApplication extends BaseApplication {
    private static instance: VApplication;

    private constructor(options: any = {}) {
        super(options);

        this.setupBindings();

        //Modules
        this.initModules();

        //Services
        this.initServices();
    }

    static getInstance(options: any = {}) {
        if (!this.instance) {
            this.instance = new VApplication(options);
        }
        return this.instance;
    }

    // @ts-ignore
    async start() {
        await this.initRunLevel();
        await this.unitTest();
    }

    setupBindings() {
        //------------->>>>>--------------//
        this.configure('services.DummyRestService', {
            baseURL: DEFAULT_APP_CONFIG?.servers.rest.dummyRestServer.baseURL,
        });
        //------------->>>>>--------------//
    }

    async initRunLevel() {
        //------------->>>>>--------------//
        const graphqlModule = this.getSync(
            'services.GraphqlServerModule',
        ) as GraphqlServerModule;
        if (graphqlModule) {
            type TClient = typeof DEFAULT_APP_CONFIG.servers.graphql[0];
            DEFAULT_APP_CONFIG.servers.graphql.forEach(
                async (client: TClient) => {
                    await graphqlModule.createClient(client.name, {
                        url: client.url,
                        getContext: this.getGraphQLContext.bind(this),
                    });
                },
            );
        }
        //------------->>>>>--------------//
    }

    async unitTest() {
        const ut = new UnitTest(VApplication);
        ut.run();
    }

    initModules() {
        this.service("services.FSMFactoryModule", FSMFactoryModule);
        this.service("services.EventBusModule", EventBusModule);
        this.service('services.GraphqlServerModule', GraphqlServerModule);
        this.service('services.StorageModule', StorageModule);
    }

    initServices() {
        this.service('services.TrafficLightService', TrafficLightService);
        this.service('services.DummyRestService', DummyRestService);
        this.service('services.DeviceInfoService', DeviceInfoService);
        this.service('services.DummyGraphQLService', DummyGraphQLService);
        this.service('services.LoginService', LoginService);
        this.service('services.SignUpService', SignUpService);
        this.service('services.ResetPasswordService', ResetPasswordService);
        this.service('services.ChangePasswordService', ChangePasswordService);
        this.service('services.ApplicationFeeDurationService', ApplicationFeeDurationService);
        this.service('services.ApplicationStatusService', ApplicationStatusService);
        this.service('services.UploadPaymentService', UploadPaymentService);
        this.service('services.GetCertificatesTableService', GetCertificatesTableService);
        this.service('services.ApplicationFormService', ApplicationFormService);
        this.service('services.ProfileService', ProfileService);
        this.service('services.AuthService', AuthService);
        this.service('services.DownloadEpccService', DownloadEpccService);
        this.service('services.EmailEpccService', EmailEpccService);
        this.service('services.ApplicationListService', ApplicationListService);
        this.service('services.UpdateApplicationFormService', UpdateApplicationFormService);
    }

    private getGraphQLContext() {
        const loginService = this.getSync("services.AuthService") as AuthService
        const deviceInfoService = this.getSync(
            'services.DeviceInfoService',
        ) as DeviceInfoService;
        console.log('graphqlContext call');
        //FIXME:
        return {
            headers: {
                authentication: loginService?.getToken(),
                headers: {
                    ...{ ...deviceInfoService?.getDeviceInfo() },
                    ...{ ...deviceInfoService?.getAppInfo() },
                },
            },
        };
    }

}
